import InputAdornment from '@mui/material/InputAdornment';
import { formatWeightValueForDisplay } from '@src/common/components/FormatWeightValueForDisplay';
import { TextInput } from '@src/common/components/TextInput';
import { useMeasurement } from '@src/common/measurements/useMeasurement';
import { poundsToKilos } from '@src/common/measurements/WeightConversions';
import type { ComponentProps } from 'react';
import {
  type ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends Omit<
  ComponentProps<typeof TextInput>,
  'value' | 'onChange'
> {
  value: number | null;
  onChange: (value: number | null) => void;
  displayUnits?: boolean;
  ariaLabel?: string;
  multiplyByThousand?: boolean;
  endAdornment?: JSX.Element;
}

export const WeightInput = forwardRef(function CustomNumberInput(
  {
    label = '',
    value,
    onChange,
    onBlur,
    ariaLabel,
    displayUnits = true,
    tabIndex,
    multiplyByThousand = false,
    endAdornment,
    ...props
  }: Props,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const [isEditing, setIsEditing] = useState(false);
  const { isMetric } = useMeasurement();
  const { t } = useTranslation();
  const toDisplay = useCallback(
    (numberValue: number | string | undefined | null) => {
      return formatWeightValueForDisplay(numberValue, isMetric);
    },
    [isMetric],
  );

  const [innerValue, setInnerValue] = useState<string>(toDisplay(value));

  useEffect(() => {
    if (!isEditing) {
      setInnerValue(toDisplay(value));
    }
  }, [isMetric, toDisplay, value, innerValue, isEditing]);

  const change = (newValue: string) => {
    setInnerValue(newValue);

    if (!newValue) {
      onChange(null);
      return;
    }
    if (new RegExp('^(\\d+([.]\\d{1,2})?|[.]\\d{0,2})$').test(newValue)) {
      onChange(toValue(newValue));
      return;
    }
  };

  const toValue = (stringValue: string) => {
    if (isMetric) {
      return parseFloat(stringValue);
    } else {
      const numericValue =
        multiplyByThousand && stringValue.length <= 2
          ? parseFloat(stringValue) * 1000
          : parseFloat(stringValue);
      return poundsToKilos(numericValue);
    }
  };

  return (
    <TextInput
      onFocus={() => {
        setIsEditing(true);
      }}
      ref={ref}
      slotProps={{
        htmlInput: {
          'aria-label': ariaLabel,
          tabIndex,
          readOnly: props.readOnly,
        },
        input: {
          startAdornment: displayUnits ? (
            <InputAdornment position="start">
              {isMetric ? t('units.kilos') : t('units.pounds')}
            </InputAdornment>
          ) : null,
          endAdornment,
        },
      }}
      label={label}
      value={innerValue}
      onBlur={(e) => {
        setInnerValue(toDisplay(value));
        setIsEditing(false);
        onBlur?.(e);
      }}
      onChange={(e) => change(e.target.value)}
      {...props}
    />
  );
});
